/**
 * @fileOverview Where a step's text sits on the stage, beside its dot. Pure math — no Vue, no DOM.
 * See docs/view-help.md.
 *
 * Everything here is in stage pixels: the region as it will be once the camera has arrived, because
 * the text only appears then.
 */

import type { ViewHelpPlacement } from "@/services/viewHelp/types"
import type { Rect } from "@/services/viewHelp/target"
import { px } from "@/services/viewHelp/camera"

/** Distance between the region and the text, and the text's minimum distance from the stage edge. */
const GAP = 14
const MARGIN = 12

/** How close to its own corner the arrow may sit before it would point past the text's edge. */
const ARROW_INSET = 14

/** Room between the dot and a bubble placed right under it, clear of the dot's 36px hit area. */
const UNDER_DOT = 22

/** Where along its facing edge the text's arrow sits: its inset, and half the arrow's own width. */
const ARROW_AT = ARROW_INSET + 10

/**
 * How far inside the region the dot sits, across and along the edge that faces the text — the same
 * "just inside the corner" as `aimFor`, measured on the stage.
 */
const DOT_IN = 22
const DOT_ALONG = 40

export type BubbleSide = Exclude<ViewHelpPlacement, "auto">

export interface Size {
  width: number
  height: number
}

export interface BubblePlacement {
  side: BubbleSide
  top: number
  left: number
  /** Where the dot goes, on the stage: inside the region, on the edge the text is on. */
  dot: { x: number; y: number }
}

const clamp = (min: number, value: number, max: number) => Math.min(Math.max(value, min), max)

/**
 * The first side of the region the text fits on, starting with the step's preference, then clamped
 * into the stage — and the dot, which follows the text.
 *
 * The text hangs from the dot: it starts at the region's near corner rather than centred on the side,
 * and the dot sits just inside the region on the edge the text is on, where the arrow points. It used
 * to sit in the region's top-left corner whatever side the text took, so on a lane of the planning
 * board — a tall, page-wide region with its text below — the dot was at one end of the page and the
 * text at the other, and nothing said they belonged together.
 *
 * A region with room on no side — a dialog shown whole fills most of the stage — gets the text inside
 * it, right under the dot, pointing up at it. The old last resort put it below the region and let the
 * clamp pull it back, which parked it against the bottom edge of the stage, far from anything.
 */
export function placeBubble(
  region: Rect,
  bubble: Size,
  stage: Size,
  preferred: ViewHelpPlacement = "auto",
  dot: { x: number; y: number } = { x: region.left, y: region.top }
): BubblePlacement {
  const { width, height } = bubble
  const order: BubbleSide[] = ["bottom", "top", "right", "left"]
  const candidates = preferred === "auto" ? order : [preferred, ...order.filter(side => side !== preferred)]

  const clampLeft = (left: number) => clamp(MARGIN, left, Math.max(MARGIN, stage.width - width - MARGIN))
  const clampTop = (top: number) => clamp(MARGIN, top, Math.max(MARGIN, stage.height - height - MARGIN))

  for (const side of candidates) {
    const fit = fitOn(side, region, bubble, stage)
    if (!fit) continue
    const left = clampLeft(fit.left)
    const top = clampTop(fit.top)
    return { side, left, top, dot: dotOn(side, region, { left, top }) }
  }

  // Inside the region, under the dot it was given.
  return { side: "bottom", left: clampLeft(dot.x - ARROW_AT), top: clampTop(dot.y + UNDER_DOT), dot }
}

/**
 * The dot for text on `side`: across, just inside the facing edge; along it, under the arrow — kept
 * inside the region, so a text the clamp pushed along still has its dot on the thing it is about.
 */
function dotOn(side: BubbleSide, region: Rect, placed: { left: number; top: number }) {
  const right = region.left + region.width
  const bottom = region.top + region.height
  const inX = Math.min(region.width / 2, DOT_IN)
  const inY = Math.min(region.height / 2, DOT_IN)
  if (side === "top" || side === "bottom") {
    return {
      x: clamp(region.left + inX, placed.left + ARROW_AT, right - inX),
      y: side === "top" ? region.top + inY : bottom - inY,
    }
  }
  return {
    x: side === "left" ? region.left + inX : right - inX,
    y: clamp(region.top + inY, placed.top + ARROW_AT, bottom - inY),
  }
}

/**
 * Where the text goes on one side of the region, or null when it does not fit there. Along the side it
 * starts at the region's near corner, with its arrow over the spot `aimFor` would pick — or over the
 * middle of a region narrower than that.
 */
function fitOn(side: BubbleSide, region: Rect, { width, height }: Size, stage: Size) {
  const alongY = region.top + Math.min(region.height / 2, DOT_ALONG) - ARROW_AT
  const alongX = region.left + Math.min(region.width / 2, DOT_ALONG) - ARROW_AT
  if (side === "right") {
    const left = region.left + region.width + GAP
    return left + width <= stage.width - MARGIN ? { left, top: alongY } : null
  }
  if (side === "left") {
    const left = region.left - GAP - width
    return left >= MARGIN ? { left, top: alongY } : null
  }
  if (side === "bottom") {
    const top = region.top + region.height + GAP
    return top + height <= stage.height - MARGIN ? { top, left: alongX } : null
  }
  const top = region.top - GAP - height
  return top >= MARGIN ? { top, left: alongX } : null
}

/**
 * The arrow's style, pointing from the text at the dot — or null rather than point at nothing, once
 * the clamp has moved the text past the dot.
 */
export function arrowStyleFor(
  placed: BubblePlacement,
  dot: { x: number; y: number },
  { width, height }: Size
): Record<string, string> | null {
  if (placed.side === "right" || placed.side === "left") {
    const offset = dot.y - placed.top
    if (offset < ARROW_INSET || offset > height - ARROW_INSET) return null
    return { top: px(offset), [placed.side === "right" ? "left" : "right"]: "-5px" }
  }
  const offset = dot.x - placed.left
  if (offset < ARROW_INSET || offset > width - ARROW_INSET) return null
  return { left: px(offset), [placed.side === "bottom" ? "top" : "bottom"]: "-5px" }
}
